import {getTestProps} from '@benchling/js-test-utils';
import {Accordion, createStyles} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {useActivityModalControls} from 'llm/chat-library/tool-details/ActivityModal';
import {isNativeLoadSkill} from 'llm/chat-library/tool-details/LoadSkillToolCallParser';
import {parseManageSkillResponse} from 'llm/chat-library/tool-details/ManageSkillToolCallParser';
import {parseProgressToolCall} from 'llm/chat-library/tool-details/ParsedProgressToolCall';
import {getToolInputBlock, getToolOutputBlock} from 'llm/chat-library/tool-details/ToolDetail';
import {getOneSentenceSummary, INTENT_SUMMARY_ARG} from 'llm/chat-library/tool-details/toolIntentSummary';
import {ToolType, getToolSearchSelectedToolNames, isToolType} from 'llm/chat-library/tool-details/types';
import {useToolDisplayName} from 'llm/chat-library/tool-details/useToolDisplayName';
import {useToolIcon} from 'llm/chat-library/tool-details/useToolIcon';
import {AI_GRAY_225} from 'llm/chat-library/ui/AIStyleConstants';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import Label from 'ui-library/bs/Label';
import {
  BLUE_DARKER,
  GRAY_DARK,
  GRAY_DARKER,
  NEAR_BLACK,
  PURPLE_LIGHTEST,
  RED_LIGHTEST,
  WHITE,
} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

import {ProgressData, ProgressToolCall} from '../progress/ProgressTypes';
import {ChatFailureIndicator} from './ChatFailureIndicator';
import {ClientProgressMessage, ProgressStatus} from './MessageTypes';
import {ProgressFailed as SharedProgressFailed} from './ProgressFailed';
import {ThinkingDuotoneSpinner, ThinkingHeader, ThinkingHeaderVariant} from './ThinkingSpinner';
import {ToolActivityItem} from './ToolActivityItem';

const TEST_COMPONENT = 'ChatProgress';
const THINKING_ACCORDION_VALUE = 'thinking';
const THINKING_ACCORDION_TRANSITION_MS = 200;
const THINKING_HEADER_TRANSITION_MS = THINKING_ACCORDION_TRANSITION_MS;

export function getProgressHeaderLabel(status: ProgressStatus): string {
  switch (status) {
    case ProgressStatus.QUEUED:
      return 'Starting agent...';
    case ProgressStatus.STARTED:
    case ProgressStatus.IN_PROGRESS:
      return 'Working';
    case ProgressStatus.COMPLETED:
    case ProgressStatus.FAILED:
      return 'Worked';
    default:
      return assertNever(status);
  }
}

// ============================================================================
// Shared Progress Components
// Used by both Deep Research and Data Capture agents
// ============================================================================

export interface ProgressMessageProps {
  message: ClientProgressMessage;
  progressData: ProgressData | null;
  onToolClick?: (toolCall: ProgressToolCall) => void;
  /**
   * Whether to display model-authored intent_summary blurbs on tool calls
   * (the LLM_ENABLE_TOOL_INTENT_SUMMARIES rollout). Defaults to true; see {@link ThinkingSection}.
   */
  showIntentSummaries?: boolean;
}

/**
 * A unified progress component for agents that shows an expandable "Thinking" section
 * with tool calls and their outputs. Used by both Deep Research and Data Capture.
 */
export function ProgressMessage({
  message,
  progressData,
  onToolClick,
  showIntentSummaries = true,
}: ProgressMessageProps): JSX.Element {
  const {status, errorMessage} = message;

  switch (status) {
    case ProgressStatus.QUEUED:
    case ProgressStatus.STARTED:
    case ProgressStatus.IN_PROGRESS:
      return (
        <ThinkingSection
          progressData={progressData}
          isComplete={false}
          progressStatus={status}
          onToolClick={onToolClick}
          errorMessage={errorMessage ?? undefined}
          showIntentSummaries={showIntentSummaries}
        />
      );
    case ProgressStatus.COMPLETED:
      if (progressData && progressData.toolCalls.length > 0) {
        return (
          <ThinkingSection
            progressData={progressData}
            isComplete={true}
            progressStatus={status}
            onToolClick={onToolClick}
            showIntentSummaries={showIntentSummaries}
            defaultExpanded={message.thinkingDefaultExpanded === true}
          />
        );
      }
      return <></>;
    case ProgressStatus.FAILED:
      return (
        <SharedProgressFailed
          title="Request failed"
          errorMessage={errorMessage || 'Could not complete request'}
          testComponent={TEST_COMPONENT}
        />
      );
    default:
      throw new Error(`Unknown progress status: ${assertNever(status)}`);
  }
}

/**
 * Get the display text for a tool call's input, if it should be shown.
 * Returns null if the tool type should not display input.
 * Only Data Capture tools have input display - Deep Research tools return null.
 *
 * `showIntentSummaries` gates display of the model-authored `intent_summary` blurb (the
 * LLM_ENABLE_TOOL_INTENT_SUMMARIES rollout). When false, tools fall back to their deterministic
 * display (e.g. search query terms) and tools whose only blurb is the summary (warehouse/code)
 * show nothing inline -- i.e. the pre-intent-summary behavior. The backend still generates
 * summaries either way; this only gates client display.
 *
 * IMPORTANT: When adding new tools, also update the backend toolkit in:
 * - benchling/llm/data_capture_assistant/agent/data_capture_agent.py (get_data_capture_agent_toolkit)
 */
function getToolInputDisplay(toolCall: ProgressToolCall, showIntentSummaries: boolean): string | null {
  const {tool, args} = toolCall;
  if (!args) {
    return null;
  }
  // Per-tool handling wins (e.g. search prefers its query terms; warehouse/code show only the
  // model's summary). Any tool without bespoke handling that still carries an intent_summary --
  // e.g. a new tool adopting the backend IntentSummaryToolMixin -- falls back to displaying that
  // summary, so adoption works end-to-end without having to add a case below.
  const specificDisplay = isToolType(tool)
    ? getToolSpecificInputDisplay(tool, args, showIntentSummaries)
    : null;
  return specificDisplay ?? (showIntentSummaries ? getOneSentenceSummary(args, INTENT_SUMMARY_ARG) : null);
}

/**
 * Per-tool inline input display. Returns null when the tool has no bespoke rendering; the caller
 * (getToolInputDisplay) then applies the generic intent_summary fallback. `showIntentSummaries`
 * gates the model-authored intent_summary blurb; see {@link getToolInputDisplay}.
 */
function getToolSpecificInputDisplay(
  tool: ToolType,
  args: Record<string, unknown>,
  showIntentSummaries: boolean
): string | null {
  switch (tool) {
    case ToolType.FillTables:
      // Show agent_instructions for FillTables
      if (typeof args.agent_instructions === 'string') {
        return args.agent_instructions;
      }
      return null;

    case ToolType.SearchSchemas:
      // Show search_queries for SearchSchemas
      if (Array.isArray(args.search_queries)) {
        return args.search_queries.join(', ');
      }
      return null;

    case ToolType.AskQuestion:
      // Show question for AskQuestion
      if (typeof args.question === 'string') {
        return args.question;
      }
      return null;

    case ToolType.ReadFiles:
      // Show analysis_prompt for ReadFiles
      if (typeof args.analysis_prompt === 'string') {
        return args.analysis_prompt;
      }
      return null;

    case ToolType.SearchFiles:
      // Show query for SearchFiles
      if (typeof args.query === 'string') {
        return args.query;
      }
      return null;

    case ToolType.SearchTool:
    case ToolType.SearchBenchlingObjects:
      // Prefer the model's intent summary (when enabled); fall back to the raw query terms.
      return (
        (showIntentSummaries ? getOneSentenceSummary(args, INTENT_SUMMARY_ARG) : null) ??
        getSearchQueryFallback(args)
      );

    case ToolType.SearchEntriesAndTemplates:
      // Prefer the model's intent summary (when enabled); fall back to the raw search query terms.
      // The summary is especially useful for filter-only searches (empty query), where there are
      // no terms to show.
      return (
        (showIntentSummaries ? getOneSentenceSummary(args, INTENT_SUMMARY_ARG) : null) ??
        getSearchQueriesFallback(args)
      );

    case ToolType.ReviewTool: {
      // Show criteria as a bulleted list and guidelines as content
      const parts: Array<string> = [];
      if (args.criteria && typeof args.criteria === 'object') {
        const criteriaValues = Object.values(args.criteria as Record<string, string>);
        if (criteriaValues.length > 0) {
          parts.push(criteriaValues.map((v) => `- ${v}`).join('\n'));
        }
      }
      if (typeof args.guidelines === 'string' && args.guidelines.trim()) {
        parts.push(args.guidelines);
      }
      return parts.length > 0 ? parts.join('\n\n') : null;
    }

    case ToolType.EditTableCells:
      // Show table_name for EditTableCells
      if (typeof args.table_name === 'string') {
        return args.table_name;
      }
      return null;

    case ToolType.LoadSkill: {
      if (typeof args.skill_id === 'string' && isNativeLoadSkill(args.skill_id)) {
        return null;
      }
      if (typeof args.file_path === 'string' && args.file_path) {
        return args.file_path;
      }
      return null;
    }

    case ToolType.ManageSkill:
      return null;

    case ToolType.ToolSearch:
      // "select:" queries surface the chosen tool names in the title (via enrichDisplayName), so
      // don't also render the raw query string as a description.
      if (getToolSearchSelectedToolNames(args).length > 0) {
        return null;
      }
      if (typeof args.query === 'string') {
        return args.query;
      }
      return null;

    case ToolType.ResearchSubAgent:
      return getOneSentenceSummary(args, 'prompt_summary');

    case ToolType.QueryWarehouseTool:
    case ToolType.ExecuteCustomCodePipelineStep:
    case ToolType.RunCodeTool:
      // Raw SQL/code is not a useful blurb, so show only the model's intent summary. Returning null
      // here defers to the generic intent_summary fallback in getToolInputDisplay; we keep an
      // explicit case so it is clear the raw query/code must never be surfaced inline.
      return null;

    case ToolType.IdsToModelsTool:
      // Bare IDs are opaque as text but meaningful as chips; render the first few as chips.
      return getIdChipSummary(args.object_ids);

    case ToolType.ReadBenchlingDocumentTool:
      // A single document ID; render it as a chip via the shared markdown renderer.
      return typeof args.document_id === 'string' && args.document_id.trim()
        ? toIdChipTag(args.document_id.trim())
        : null;

    case ToolType.WebSearch:
      // WebSearch has no model-authored intent_summary (it isn't an IntentSummaryToolMixin tool);
      // the search query is the meaningful inline detail.
      return getSearchQueryFallback(args);

    // TODO: Add brief input summaries for these tools.
    case ToolType.Think:
    case ToolType.PublicDataSubAgent:
    case ToolType.GetWarehouseOverviewTool:
    case ToolType.SearchNotebookEntries:
    case ToolType.SearchSequencesTool:
    case ToolType.AttachmentTool:
    case ToolType.PlottingTool:
    case ToolType.GenerateImageTool:
    case ToolType.CustomCodeTool:
    case ToolType.ReadTargetDocument:
    case ToolType.ReadDraftDocument:
    case ToolType.InstantiateDocument:
    case ToolType.TranscribeFile:
    case ToolType.UpdateDocument:
    case ToolType.InsertDocumentContent:
    case ToolType.CreateLookupConfig:
    case ToolType.CreateAnalysisPlan:
    case ToolType.SaveArtifact:
    case ToolType.ListArtifacts:
    case ToolType.ViewArtifact:
    case ToolType.LoadFileAsArtifact:
    case ToolType.LoadDocumentAttachmentAsArtifact:
    case ToolType.SubstituteInArtifact:
    case ToolType.UploadFileToSandbox:
    case ToolType.LoadDropdownOptions:
    case ToolType.HelpDocs:
    case ToolType.LoadPackageDocs:
    case ToolType.AskWarehouse:
    case ToolType.SleepTool:
    // Co-scientist tools: no brief inline summary yet.
    // falls through
    case ToolType.ListFolderContents:
    case ToolType.CreateFolder:
    case ToolType.CreateEntry:
    case ToolType.CreateFile:
    case ToolType.UpdateFile:
    case ToolType.ReadFile:
    case ToolType.CreateAASequence:
    case ToolType.CreateMolecule:
    case ToolType.CreateCustomEntities:
    case ToolType.UpdateCustomEntities:
    case ToolType.ArchiveItem:
    case ToolType.RenameItem:
    case ToolType.MoveItemToFolder:
    case ToolType.SetTargetEntry:
    case ToolType.CreateDocumentTool:
    case ToolType.InvokeToolsFromFile:
    case ToolType.RegisterEntities:
    case ToolType.TransferIntoContainers:
    case ToolType.CreatePlate:
    case ToolType.StartChatSession:
    case ToolType.GetChatSession:
    case ToolType.ScheduleForFuture:
    case ToolType.CouncilTool:
    case ToolType.CreateProcedureRunPlan:
    case ToolType.ValidateProcedureRunPlan:
    case ToolType.PromoteProcedureRunPlan:
    case ToolType.UpdateProcedureRunPlanConditions:
    case ToolType.AddProcedureRunPlanConditions:
    case ToolType.DeleteProcedureRunPlanConditions:
    case ToolType.UpdateProcedureRunPlanConditionReplicates:
    case ToolType.CreateProcedureRunParameterValue:
    // Structure prediction tools: no brief inline summary yet.
    // falls through
    case ToolType.ListStructurePredictionModels:
    case ToolType.StartStructurePredictionBatch:
    case ToolType.GetStructurePredictionBatchResults:
      return null;

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
    case ToolType.Respond:
    case ToolType.ChatInterruptProgress:
      return null;
    default:
      assertNever(tool);
      return null;
  }
}

/**
 * Deterministic fallback for search tools when the model omits an intent summary: join the raw
 * query terms (list or single string) into a display string, or null if absent/empty.
 */
function getSearchQueryFallback(args: Record<string, unknown>): string | null {
  if (Array.isArray(args.query)) {
    const query = args.query.filter(
      (item): item is string => typeof item === 'string' && Boolean(item.trim())
    );
    return query.length > 0 ? query.join(', ') : null;
  }
  if (typeof args.query === 'string') {
    const query = args.query.trim();
    return query || null;
  }
  return null;
}

/**
 * Deterministic fallback for the notebook-entry search tools, which take their terms in a
 * `search_queries` list rather than `query`. Returns the joined terms, or null when absent/empty
 * (e.g. a filter-only search), in which case the caller relies on the intent summary instead.
 */
function getSearchQueriesFallback(args: Record<string, unknown>): string | null {
  if (Array.isArray(args.search_queries)) {
    const queries = args.search_queries.filter(
      (item): item is string => typeof item === 'string' && Boolean(item.trim())
    );
    return queries.length > 0 ? queries.join(', ') : null;
  }
  return null;
}

/**
 * The most IDs to render inline as chips for ID-input tools (IdsToModelsTool); the rest are
 * summarized as a "+N more" suffix to keep the always-visible progress line compact. The
 * activity modal shows the full list.
 */
const MAX_INLINE_ID_CHIPS = 3;

/**
 * Wrap a Benchling API ID in a `<bnch_id>` markdown tag so the shared markdown renderer
 * (LLMMarkdownSection, used by ToolDetailContent) resolves it to an entity chip. Unsupported
 * or malformed IDs degrade to plain ID text without leaking the literal tag.
 */
function toIdChipTag(id: string): string {
  return `<bnch_id>${id}</bnch_id>`;
}

interface IdChipSummaryParts {
  /** Markdown for the first few IDs as `<bnch_id>` chip tags (no overflow suffix). */
  chipsMarkdown: string;
  /** How many IDs beyond the inline chips were collapsed into the "+N more" overflow. */
  overflowCount: number;
}

/**
 * Split a list of Benchling API IDs into the inline chip markdown (first few IDs) and the overflow
 * count, or null when there are no usable IDs. Keeping the overflow count separate lets the caller
 * render it as a clickable "+N more" link rather than baking it into the chip markdown.
 */
function getIdChipSummaryParts(rawIds: unknown): IdChipSummaryParts | null {
  if (!Array.isArray(rawIds)) {
    return null;
  }
  const ids = rawIds.filter((id): id is string => typeof id === 'string' && Boolean(id.trim()));
  if (ids.length === 0) {
    return null;
  }
  const chipsMarkdown = ids
    .slice(0, MAX_INLINE_ID_CHIPS)
    .map((id) => toIdChipTag(id))
    .join(', ');
  return {chipsMarkdown, overflowCount: Math.max(0, ids.length - MAX_INLINE_ID_CHIPS)};
}

/**
 * Build a compact, chip-rendering summary string for a list of Benchling API IDs: the first few IDs
 * as chips, plus a "+N more" suffix when the list is longer. Returns null when there are no usable
 * IDs. This is the plain-text form; inline rendering in the progress section uses
 * {@link IdsToModelsInlineDisplay} so the overflow can be a clickable link.
 */
function getIdChipSummary(rawIds: unknown): string | null {
  const parts = getIdChipSummaryParts(rawIds);
  if (parts == null) {
    return null;
  }
  return parts.overflowCount > 0
    ? `${parts.chipsMarkdown} +${parts.overflowCount} more`
    : parts.chipsMarkdown;
}

interface ReviewIssueData {
  description?: string;
  severity?: string;
  location?: string;
  corrected_value?: string;
}

interface ReviewOutputData {
  status?: string;
  correctness?: number;
  completeness?: number;
  iteration?: number;
  issues?: Array<ReviewIssueData>;
}

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
};

/**
 * Parse the review tool's JSON output into a readable markdown summary.
 * Shows status, scores, iteration count, and issues with descriptions.
 */
function formatReviewOutput(responseText: string): string | null {
  let data: ReviewOutputData;
  try {
    data = JSON.parse(responseText) as ReviewOutputData;
  } catch (error) {
    // JSON.parse throws SyntaxError for invalid JSON
    if (error instanceof SyntaxError) {
      return null;
    }
    throw error;
  }

  const lines: Array<string> = [];

  // Status
  if (data.status) {
    const statusLabels: Record<string, string> = {
      PASSED: 'Passed',
      NEEDS_IMPROVEMENT: 'Needs improvement',
      FLAGGED: 'Flagged for review',
    };
    lines.push(`**${statusLabels[data.status] ?? data.status}**`);
  }

  // Scores
  if (data.correctness != null && data.completeness != null) {
    lines.push(`Correctness: ${data.correctness}% · Completeness: ${data.completeness}%`);
  }

  // Iteration
  if (data.iteration != null) {
    lines.push(`Iteration: ${data.iteration}`);
  }

  // Issues with descriptions
  const issues = data.issues ?? [];
  if (issues.length > 0) {
    const issueLines = issues.map((issue) => {
      const sev = (issue.severity ?? 'minor').toLowerCase();
      const label = SEVERITY_LABELS[sev] ?? sev;
      const location = issue.location ? ` (${issue.location})` : '';
      const corrected = issue.corrected_value ? ` → \`${issue.corrected_value}\`` : '';
      return `- **${label}**: ${issue.description ?? 'No description'}${location}${corrected}`;
    });
    lines.push(`**Issues (${issues.length}):**\n${issueLines.join('\n')}`);
  } else {
    lines.push('No issues found');
  }

  return lines.length > 0 ? lines.join('\n\n') : null;
}

/**
 * Get the display text for a tool call's output, if it should be shown.
 * Returns null if the tool type should not display output.
 * Only Data Capture tools have output display - Deep Research tools return null.
 */
function getToolOutputDisplay(toolCall: ProgressToolCall): string | null {
  const {tool, responseText} = toolCall;
  if (!responseText) {
    return null;
  }
  if (!isToolType(tool)) {
    return null;
  }

  switch (tool) {
    case ToolType.FillTables:
      // Show full output for FillTables
      return responseText;

    case ToolType.SearchSchemas: {
      // Parse output to show count of schemas found
      const schemaMatch = responseText.match(/Found (\d+) (?:entity )?schema/i);
      if (schemaMatch) {
        return `Found ${schemaMatch[1]} schema${schemaMatch[1] === '1' ? '' : 's'}`;
      }
      // Fallback: try to count schema names in the response
      const schemaCount = (responseText.match(/Schema Name:/g) || []).length;
      if (schemaCount > 0) {
        return `Found ${schemaCount} schema${schemaCount === 1 ? '' : 's'}`;
      }
      return null;
    }

    case ToolType.Think:
      // Show full output for Think
      return responseText;

    case ToolType.AskQuestion:
      // AskQuestion responses are not displayed in the output section because
      // they contain internal search results that are not meaningful to show
      // in the progress UI. The agent uses these results internally.
      return null;

    case ToolType.ReadFiles:
      // Show full output for ReadFiles
      return responseText;

    case ToolType.ReviewTool:
      return formatReviewOutput(responseText);

    case ToolType.ToolSearch:
      // The response is an internal <functions> schema dump, not meaningful progress text (and it's
      // long enough to trigger a "Show more" toggle). The loaded tools are already shown in the
      // title, so suppress the inline output. The full response is still available in the activity
      // modal via getToolOutputBlock.
      return null;
    case ToolType.SleepTool:
      return null;

    case ToolType.ResearchSubAgent:
      // The full subagent response belongs in the activity modal; inline progress shows only the
      // prompt summary from getToolInputDisplay.
      return null;

    // TODO: Add brief output summaries for these tools.
    case ToolType.IdsToModelsTool:
    case ToolType.ReadTargetDocument:
    case ToolType.ReadDraftDocument:
    case ToolType.ReadBenchlingDocumentTool:
    case ToolType.PublicDataSubAgent:
    case ToolType.WebSearch:
    case ToolType.SearchTool:
    case ToolType.GetWarehouseOverviewTool:
    case ToolType.SearchNotebookEntries:
    case ToolType.SearchSequencesTool:
    case ToolType.QueryWarehouseTool:
    case ToolType.AttachmentTool:
    case ToolType.PlottingTool:
    case ToolType.GenerateImageTool:
    case ToolType.CustomCodeTool:
    case ToolType.ExecuteCustomCodePipelineStep:
    case ToolType.RunCodeTool:
    case ToolType.InstantiateDocument:
    case ToolType.SearchEntriesAndTemplates:
    case ToolType.SearchBenchlingObjects:
    case ToolType.TranscribeFile:
    case ToolType.EditTableCells:
    case ToolType.UpdateDocument:
    case ToolType.InsertDocumentContent:
    case ToolType.SearchFiles:
    case ToolType.CreateLookupConfig:
    case ToolType.LoadSkill:
    case ToolType.CreateAnalysisPlan:
    case ToolType.SaveArtifact:
    case ToolType.ListArtifacts:
    case ToolType.ViewArtifact:
    case ToolType.LoadFileAsArtifact:
    case ToolType.LoadDocumentAttachmentAsArtifact:
    case ToolType.SubstituteInArtifact:
    case ToolType.UploadFileToSandbox:
    case ToolType.LoadDropdownOptions:
    case ToolType.HelpDocs:
    case ToolType.LoadPackageDocs:
    case ToolType.AskWarehouse:
    // Co-scientist tools: no brief inline output summary yet.
    // falls through
    case ToolType.ListFolderContents:
    case ToolType.CreateFolder:
    case ToolType.CreateEntry:
    case ToolType.CreateFile:
    case ToolType.UpdateFile:
    case ToolType.ReadFile:
    case ToolType.CreateAASequence:
    case ToolType.CreateMolecule:
    case ToolType.CreateCustomEntities:
    case ToolType.UpdateCustomEntities:
    case ToolType.ArchiveItem:
    case ToolType.RenameItem:
    case ToolType.MoveItemToFolder:
    case ToolType.SetTargetEntry:
    case ToolType.CreateDocumentTool:
    case ToolType.InvokeToolsFromFile:
    case ToolType.RegisterEntities:
    case ToolType.TransferIntoContainers:
    case ToolType.CreatePlate:
    case ToolType.StartChatSession:
    case ToolType.GetChatSession:
    case ToolType.ScheduleForFuture:
    case ToolType.CouncilTool:
    case ToolType.CreateProcedureRunPlan:
    case ToolType.ValidateProcedureRunPlan:
    case ToolType.PromoteProcedureRunPlan:
    case ToolType.UpdateProcedureRunPlanConditions:
    case ToolType.AddProcedureRunPlanConditions:
    case ToolType.DeleteProcedureRunPlanConditions:
    case ToolType.UpdateProcedureRunPlanConditionReplicates:
    case ToolType.CreateProcedureRunParameterValue:
    // Structure prediction tools: no brief inline output summary yet.
    // falls through
    case ToolType.ListStructurePredictionModels:
    case ToolType.StartStructurePredictionBatch:
    case ToolType.GetStructurePredictionBatchResults:
      return null;

    case ToolType.ManageSkill: {
      const parsed = parseManageSkillResponse(responseText);
      if (!parsed) {
        return null;
      }
      return parsed.success ? parsed.skillName : parsed.message;
    }

    // N/A - these tools are not found in TOOLS_TO_DISPLAY.
    case ToolType.CompleteAnswer:
    case ToolType.CompleteAnswerTool:
    case ToolType.DeepResearchTool:
    case ToolType.Respond:
    case ToolType.ChatInterruptProgress:
      return null;
    default:
      assertNever(tool);
      return null;
  }
}

/**
 * Renders a tool call in the thinking section.
 *
 * Clickability is determined by whether the tool has structured detail in the
 * activity modal (getToolInputBlock/getToolOutputBlock return non-null).
 * If it does, clicking opens the modal. Inline detail text (getToolInputDisplay/
 * getToolOutputDisplay) is always shown when available, independent of clickability.
 */
function ToolCallItem({
  toolCall,
  onToolClick,
  testKey,
  showIntentSummaries,
}: {
  toolCall: ProgressToolCall;
  onToolClick?: (toolCall: ProgressToolCall) => void;
  testKey?: string;
  showIntentSummaries: boolean;
}): JSX.Element {
  const getToolDisplayNameFormatted = useToolDisplayName();
  const getToolIconResolved = useToolIcon();
  const resolvedDisplayName = getToolDisplayNameFormatted(toolCall.tool, toolCall.args, toolCall.status);
  const inputDisplay = getToolInputDisplay(toolCall, showIntentSummaries);
  const outputDisplay = getToolOutputDisplay(toolCall);

  const hasStructuredDetail = useMemo(() => {
    const parsed = parseProgressToolCall(toolCall);
    return getToolInputBlock(toolCall, parsed) != null || getToolOutputBlock(toolCall, parsed) != null;
  }, [toolCall]);
  const isClickable = hasStructuredDetail && onToolClick != null;
  const handleClick = onToolClick
    ? () => {
        onToolClick(toolCall);
      }
    : undefined;

  // IdsToModelsTool renders its inputs as entity chips with a "+N more" overflow; render it through a
  // dedicated component so the overflow can be a link that opens the activity modal (full ID list).
  const idChipParts =
    toolCall.tool === ToolType.IdsToModelsTool ? getIdChipSummaryParts(toolCall.args?.object_ids) : null;

  return (
    <div
      className={css(progressStyles.toolItem)}
      {...getTestProps({component: TEST_COMPONENT, element: 'toolItem'})}
    >
      <ToolActivityItem
        icon={getToolIconResolved(toolCall.tool)}
        displayName={resolvedDisplayName}
        status={toolCall.status}
        onClick={isClickable ? handleClick : undefined}
        testComponent={TEST_COMPONENT}
        testKey={testKey}
      />
      {idChipParts ? (
        <div
          className={css(progressStyles.toolDetailText)}
          {...getTestProps({component: TEST_COMPONENT, element: 'toolInput'})}
        >
          <IdsToModelsInlineDisplay
            chipsMarkdown={idChipParts.chipsMarkdown}
            overflowCount={idChipParts.overflowCount}
            onMoreClick={handleClick}
          />
        </div>
      ) : (
        inputDisplay && (
          <div
            className={css(progressStyles.toolDetailText)}
            {...getTestProps({component: TEST_COMPONENT, element: 'toolInput'})}
          >
            <ToolDetailContent text={inputDisplay} />
          </div>
        )
      )}
      {outputDisplay && (
        <div
          className={css(progressStyles.toolDetailText)}
          {...getTestProps({component: TEST_COMPONENT, element: 'toolOutput'})}
        >
          <ToolDetailContent text={outputDisplay} />
        </div>
      )}
    </div>
  );
}

/**
 * Inline display for IdsToModelsTool in the progress section: the first few looked-up entities as
 * chips, followed by a "+N more" overflow. When `onMoreClick` is provided the overflow is a link
 * that opens the activity modal to the full ID list; otherwise it degrades to plain text.
 */
function IdsToModelsInlineDisplay({
  chipsMarkdown,
  overflowCount,
  onMoreClick,
}: {
  chipsMarkdown: string;
  overflowCount: number;
  onMoreClick?: () => void;
}): JSX.Element {
  const {classes} = useMarkdownContentStyles();
  const moreLabel = `+${overflowCount} more`;
  return (
    <div className={css(progressStyles.idChipInlineRow)}>
      <div className={classes.markdownContent}>
        <LLMMarkdownSection markdown={chipsMarkdown} />
      </div>
      {overflowCount > 0 &&
        (onMoreClick ? (
          <button
            type="button"
            className={css(progressStyles.moreLink)}
            onClick={onMoreClick}
            {...getTestProps({component: TEST_COMPONENT, element: 'idChipMore'})}
          >
            {moreLabel}
          </button>
        ) : (
          <span>{moreLabel}</span>
        ))}
    </div>
  );
}

interface ToolDetailContentProps {
  text: string;
}

const MAX_DETAIL_LENGTH = 500;

function ToolDetailContent({text}: ToolDetailContentProps): JSX.Element {
  const {classes} = useMarkdownContentStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldTruncate = text.length > MAX_DETAIL_LENGTH;
  const displayText = shouldTruncate && !isExpanded ? `${text.slice(0, MAX_DETAIL_LENGTH)}...` : text;

  return (
    <div className={css(progressStyles.detailContentWrapper)}>
      <div className={classes.markdownContent}>
        <LLMMarkdownSection markdown={displayText} />
      </div>
      {shouldTruncate && (
        <button
          type="button"
          className={css(progressStyles.expandButton)}
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

export interface ThinkingSectionProps {
  progressData: ProgressData | null;
  isComplete: boolean;
  progressStatus: ProgressStatus;
  onToolClick?: (toolCall: ProgressToolCall) => void;
  errorMessage?: string;
  /** When true, completed sections start expanded (e.g. after the user stops the assistant). */
  defaultExpanded?: boolean;
  /**
   * Whether to display model-authored intent_summary blurbs on tool calls
   * (the LLM_ENABLE_TOOL_INTENT_SUMMARIES rollout). Defaults to true so isolated renders
   * (Storybook, focused tests) keep showing summaries without wiring up LaunchDarkly; production
   * threads the resolved flag down from {@link ChatProgressMessageBubble}.
   */
  showIntentSummaries?: boolean;
}

/**
 * Unified thinking section component for both in-progress and completed states.
 * - In progress: shows a header spinner unless a visible tool is executing; always expanded
 * - Completed: shows caret button, collapsible (default collapsed)
 */
export function ThinkingSection({
  progressData,
  isComplete,
  progressStatus,
  onToolClick,
  errorMessage,
  defaultExpanded = false,
  showIntentSummaries = true,
}: ThinkingSectionProps): JSX.Element {
  const {classes: accordionClasses} = useThinkingAccordionStyles();
  const [isExpanded, setIsExpanded] = useState(() => (isComplete ? defaultExpanded : true));
  const hasUserManuallyToggledAccordion = useRef(false);
  const wasCompleteRef = useRef(isComplete);

  useEffect(() => {
    const wasComplete = wasCompleteRef.current;
    wasCompleteRef.current = isComplete;

    if (wasComplete && !isComplete) {
      hasUserManuallyToggledAccordion.current = false;
      setIsExpanded(true);
      return;
    }

    if (
      !wasComplete &&
      isComplete &&
      isExpanded &&
      !defaultExpanded &&
      !hasUserManuallyToggledAccordion.current
    ) {
      setIsExpanded(false);
    }
  }, [isComplete, isExpanded, defaultExpanded]);

  const toolCount = progressData?.toolCalls.length ?? 0;
  const hasToolCalls = toolCount > 0;
  const stepCountText = toolCount > 0 ? ` · ${toolCount} ${toolCount === 1 ? 'step' : 'steps'}` : '';
  const headerLabel = getProgressHeaderLabel(progressStatus);
  const thinkingHeaderVariant: ThinkingHeaderVariant = isComplete || hasToolCalls ? 'compact' : 'large';
  const showHeaderSpinner = !isComplete;

  const accordionValue = !isComplete || isExpanded ? THINKING_ACCORDION_VALUE : null;

  return (
    <div
      className={css(progressStyles.completedContainer)}
      {...getTestProps({component: TEST_COMPONENT, element: 'root'})}
    >
      <Accordion
        unstyled={true}
        value={accordionValue}
        onChange={(value) => {
          if (!isComplete) {
            return;
          }
          hasUserManuallyToggledAccordion.current = true;
          setIsExpanded(value === THINKING_ACCORDION_VALUE);
        }}
        transitionDuration={THINKING_ACCORDION_TRANSITION_MS}
        chevronPosition="left"
        disableChevronRotation={!isComplete}
        chevron={
          isComplete ? (
            <BenchlingIcon icon="caret-right" className={css(progressStyles.expandIcon)} />
          ) : showHeaderSpinner ? (
            <div {...getTestProps({component: TEST_COMPONENT, element: 'thinkingSpinner'})}>
              <ThinkingDuotoneSpinner />
            </div>
          ) : null
        }
        classNames={{
          item: accordionClasses.item,
          control: accordionClasses.control,
          chevron: accordionClasses.chevron,
          label: accordionClasses.label,
          content: accordionClasses.content,
          panel: accordionClasses.panel,
        }}
      >
        <Accordion.Item value={THINKING_ACCORDION_VALUE}>
          <Accordion.Control
            className={css(progressStyles.completedHeader)}
            {...(isComplete ? getTestProps({component: TEST_COMPONENT, element: 'expandButton'}) : {})}
          >
            <ThinkingHeader
              variant={thinkingHeaderVariant}
              headerLabel={headerLabel}
              stepCountText={stepCountText}
              testComponent={TEST_COMPONENT}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <div
              className={css(progressStyles.expandedContentBase)}
              {...getTestProps({component: TEST_COMPONENT, element: 'content'})}
            >
              {!isComplete && progressStatus !== ProgressStatus.QUEUED && toolCount === 0 ? (
                <Label className={css(progressStyles.analyzingText)}>Planning next steps...</Label>
              ) : progressData && toolCount > 0 ? (
                progressData.toolCalls.map((toolCall, index) => (
                  <ToolCallItem
                    key={toolCall.toolId || index}
                    toolCall={toolCall}
                    onToolClick={onToolClick}
                    testKey={`${index}`}
                    showIntentSummaries={showIntentSummaries}
                  />
                ))
              ) : null}
            </div>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      {errorMessage && (
        <div className={css(progressStyles.errorContent)}>
          <span className={css(agentProgressStyles.errorMessage)}>{errorMessage}</span>
        </div>
      )}
    </div>
  );
}

const useThinkingAccordionStyles = createStyles(() => ({
  item: {
    border: 'none',
    backgroundColor: 'transparent',
  },
  control: {
    padding: 0,
    gap: 0,
    border: 'none',
    backgroundColor: 'transparent',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  label: {
    padding: 0,
    margin: 0,
  },
  chevron: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    margin: 0,
    '&[data-rotate]': {
      transform: 'rotate(90deg)',
      transition: `transform ${THINKING_ACCORDION_TRANSITION_MS}ms ease-in-out`,
    },
  },
  content: {
    padding: 0,
  },
  panel: {
    padding: 0,
  },
}));

/**
 * Styles for the inline tool-detail markdown (intent summaries, search terms, ReviewTool output,
 * tool responseText). This must be `createStyles` (emotion), not Aphrodite: `LLMMarkdownSection`'s
 * own styling relies on descendant selectors, and Aphrodite's `StyleSheet.create` silently drops
 * `&`-nested selectors (it only handles `:` pseudo and `@media` keys). emotion emits them, letting
 * us both style the markdown blocks and override the section's defaults.
 */
const useMarkdownContentStyles = createStyles(() => ({
  markdownContent: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
    background: 'transparent',
    lineHeight: 1.5,
    wordBreak: 'break-word',
    '& section': {
      background: 'transparent',
    },
    // LLMMarkdownSection renders each block (paragraphs become a div with a 1em block margin) inside
    // a <section>. Collapse the outer margins so a single-line detail -- e.g. an intent summary or
    // search-query fallback -- adds no dead vertical space, while inter-block spacing is preserved
    // for multi-block content (e.g. ReviewTool output). The block margin is set with `!important`,
    // so the override must be `!important` to win.
    '& section > :first-child': {
      marginTop: '0 !important',
    },
    '& section > :last-child': {
      marginBottom: '0 !important',
    },
    '& ul, & ol': {
      margin: '0.5em 0',
      paddingLeft: '1.5em',
    },
    '& li': {
      margin: '0.25em 0',
    },
    '& code': {
      background: 'rgba(0, 0, 0, 0.05)',
      padding: '2px 4px',
      borderRadius: '3px',
      fontSize: '0.9em',
    },
    '& pre': {
      background: 'rgba(0, 0, 0, 0.05)',
      padding: '8px',
      borderRadius: '4px',
      overflow: 'auto',
      margin: '0.5em 0',
    },
    '& pre code': {
      background: 'transparent',
      padding: 0,
    },
    '& table': {
      borderCollapse: 'collapse',
      margin: '0.5em 0',
    },
    '& th, & td': {
      border: '1px solid rgba(0, 0, 0, 0.1)',
      padding: '4px 8px',
    },
    '& th': {
      background: 'rgba(0, 0, 0, 0.05)',
    },
  },
}));

// ============================================================================
// Chat Progress Message Bubble
// Entry point for rendering progress messages in the chat
// ============================================================================

/**
 * Render a ClientProgressMessage taking status into account.
 *
 * If the message indicates a progress status, show a loading message.
 * If the message indicates a completed status, show nothing.
 * If the message indicates a failed status, show a failure indicator.
 */
export function ActiveChatProgressMessageBubble({
  message,
  inProgressText = 'Processing your request...',
}: {
  message: ClientProgressMessage;
  inProgressText?: string | Array<string>;
}): JSX.Element | null {
  switch (message.status) {
    case ProgressStatus.QUEUED:
      return (
        <div className={css(agentProgressStyles.progressContainer)}>
          <LLMLoadingMessage text="Starting agent..." fontSize={14} />
        </div>
      );
    case ProgressStatus.STARTED:
    case ProgressStatus.IN_PROGRESS:
      return (
        <div className={css(agentProgressStyles.progressContainer)}>
          <LLMLoadingMessage text={inProgressText} fontSize={14} />
        </div>
      );
    case ProgressStatus.COMPLETED:
      return null;
    case ProgressStatus.FAILED:
      return <ChatFailureIndicator errorMessage={message.errorMessage} />;
    default:
      assertNever(message.status);
      return null;
  }
}

/**
 * Render the appropriate progress message bubble based on the agent type.
 */
export function ChatProgressMessageBubble({
  message,
  progressData,
}: {
  message: ClientProgressMessage;
  progressData: ProgressData | null;
}): JSX.Element {
  const {openActivityModal} = useActivityModalControls();
  // Display-only gate for model-authored intent_summary blurbs; the backend generates them
  // regardless. Resolved here at the bubble boundary and threaded down so the inner progress
  // components stay free of LaunchDarkly wiring (and isolated renders keep working).
  const {llmEnableToolIntentSummaries} = useRolloutFlags();
  const showIntentSummaries = llmEnableToolIntentSummaries === true;

  // For active states (STARTED/IN_PROGRESS), only use message's progressData to show
  // current exchange tool calls. Don't fall back to progressData prop which contains ALL tool calls.
  // For COMPLETED status, we can fall back to progressData prop since it's used for completed sections
  // that may need access to all tool calls.
  const effectiveProgressData =
    message.status === ProgressStatus.COMPLETED
      ? (message.progressData ?? progressData ?? null)
      : (message.progressData ?? null);

  const handleToolClick = (toolCall: ProgressToolCall): void => {
    if (effectiveProgressData) {
      openActivityModal(effectiveProgressData, toolCall.toolId);
    }
  };

  switch (message.agentType) {
    case AgentType.DEEP_RESEARCH:
    case AgentType.DATA_CAPTURE:
    case AgentType.ONBOARDING:
    case AgentType.LEGACY_DATA_IMPORT:
    case AgentType.CO_SCIENTIST:
    case AgentType.ASSISTANT:
    case AgentType.ANALYSIS:
      return (
        <ProgressMessage
          message={message}
          progressData={effectiveProgressData}
          onToolClick={handleToolClick}
          showIntentSummaries={showIntentSummaries}
        />
      );
    case AgentType.ASK:
      return (
        <ActiveChatProgressMessageBubble
          message={message}
          inProgressText={[
            'Processing your request...',
            'Analyzing your question...',
            'Gathering information...',
            'Generating answer...',
          ]}
        />
      );
    case null:
      return <ActiveChatProgressMessageBubble message={message} />;
    default:
      assertNever(message.agentType);
      return <ActiveChatProgressMessageBubble message={message} />;
  }
}

// ============================================================================
// Styles
// ============================================================================

const TEXT_COLOR = NEAR_BLACK;

// A tool-call row lays out as `[icon][gap][name]` (see agentProgressStyles.activityItem). The
// inline detail beneath the name must indent past both the icon and the gap to line up under the
// name, so derive its left margin from the same icon-size and gap values.
const TOOL_ICON_SIZE_PX = 16;
const TOOL_ROW_GAP_PX = 12;
const TOOL_DETAIL_INDENT_PX = TOOL_ICON_SIZE_PX + TOOL_ROW_GAP_PX;

/**
 * Styles specific to the unified progress components.
 */
const progressStyles = StyleSheet.create({
  completedContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '12px 0',
    gap: '0',
    backgroundColor: WHITE,
  },

  completedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
  },

  completedText: {
    color: TEXT_COLOR,
    margin: 0,
  },

  inProgressHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  stepCountText: {
    ...FONT_PROXIMA_14,
    color: TEXT_COLOR,
    margin: 0,
  },

  expandIcon: {
    color: TEXT_COLOR,
    fontSize: '12px',
  },

  analyzingText: {
    color: TEXT_COLOR,
    margin: 0,
  },

  errorContent: {
    marginTop: '8px',
  },

  // Base expanded content style (tool list) with vertical line
  expandedContentBase: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '8px',
    marginLeft: 3, // Align the vertical line with the chevron.
    paddingLeft: '16px',
    borderLeft: `1px dashed ${AI_GRAY_225}`,
    background: 'transparent',
  },

  // Tool item styles
  toolItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    background: 'transparent',
  },

  toolName: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: GRAY_DARKER,
    background: 'transparent',
    margin: 0,
  },

  toolDetailText: {
    ...FONT_PROXIMA_12,
    color: GRAY_DARKER,
    marginTop: '4px',
    // Align the detail under the tool name (past the icon + row gap), not the icon.
    marginLeft: TOOL_DETAIL_INDENT_PX,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },

  detailContentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },

  expandButton: {
    ...FONT_PROXIMA_12,
    color: BLUE_DARKER,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    marginTop: '4px',
    textAlign: 'left',
    ':hover': {
      textDecoration: 'underline',
    },
  },

  // Keeps the entity chips and the "+N more" link on one inline, wrapping row.
  idChipInlineRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px',
    background: 'transparent',
  },

  moreLink: {
    ...FONT_PROXIMA_12,
    color: BLUE_DARKER,
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});

/**
 * Shared styles for agent progress components.
 * Used by deep research and data capture progress UIs.
 */
export const agentProgressStyles = StyleSheet.create({
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '8px',
    backgroundColor: PURPLE_LIGHTEST,
  },

  failedContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '4px',
    padding: '16px',
    gap: '4px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    backgroundColor: RED_LIGHTEST,
  },

  progressHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: '8px',
  },

  thinkingSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '20px',
  },

  thinkingHeader: {
    display: 'flex',
    alignItems: 'center',
  },

  thinkingLabel: {
    color: NEAR_BLACK,
    margin: 0,
    transition: `font-size ${THINKING_HEADER_TRANSITION_MS}ms ease, line-height ${THINKING_HEADER_TRANSITION_MS}ms ease, font-weight ${THINKING_HEADER_TRANSITION_MS}ms ease`,
  },

  thinkingLabelLarge: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },

  thinkingLabelCompact: {
    ...FONT_PROXIMA_12,
    // Match bootstrap `label { font-weight: bold }` inherited by the pre-refactor compact header.
    fontWeight: 700,
  },

  errorMessage: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },

  activityContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: TOOL_ROW_GAP_PX,
    color: GRAY_DARK,
  },

  activityItemClickable: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
    borderRadius: '4px',
  },

  activityIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: TOOL_ICON_SIZE_PX,
    height: TOOL_ICON_SIZE_PX,
    flexShrink: 0,
    textAlign: 'center',
    color: GRAY_DARKER,
  },

  activityExecutingIcon: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    fontSize: '16px',
    height: TOOL_ICON_SIZE_PX,
    justifyContent: 'center',
    width: TOOL_ICON_SIZE_PX,
  },

  activityIconImg: {
    width: TOOL_ICON_SIZE_PX,
    height: TOOL_ICON_SIZE_PX,
    objectFit: 'contain' as const,
  },

  activityDescription: {
    ...FONT_PROXIMA_14,
    fontSize: '13px',
    color: GRAY_DARKER,
    flex: 1,
  },

  toolDescription: {
    alignItems: 'center',
    display: 'flex',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    minHeight: '16px',
  },
});
